import { Edit } from '@mui/icons-material';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import PermissionTooltip from 'components/Permission/PermissionTooltip';
import { useRoleBasedAccess } from 'hooks/useRoleBasedAccess';
import { ThemePalette } from 'mui.theme';
import React, { useMemo, useState } from 'react';
import {
  type CumulativeOverview,
  type EolDates,
} from 'types/survivalAnalysis.types';
import { SurvivalAnalysisDetailsChart } from 'views/SurvivalAnalysis/Helper/Modal/SurvivalAnalysis.details.chart';
import { addDaysToDate, formatAgeInDays } from '../Function/helper.function';
import { GetIcons } from '../Icons/GetIcons.function';
import componentsImage from '../Icons/componenent.png';
import assetImage from '../Icons/vehicle.png';
import SurvivalAnalysisEditModal from './SurvivalAnalysis.edit.modal';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
interface SurvivalAnalysisDetailsCardProps {
  components: any[];
  assetsPerSubComponent: CumulativeOverview[];
  enableDarkTheme: boolean;
  displayMode: string;
  detailType: string;
  eolDates?: EolDates[];
  handleModalSuccess: () => void;
  healthData: any[];
}

const backgroundColor = (workOrderAgeCategory: string): string => {
  switch (workOrderAgeCategory) {
    case 'eol':
      return 'rgba(204, 75, 55, 0.4)';
    case 'low':
      return 'rgba(107, 165, 131, 0.4)';
    case 'med':
      return 'rgba(209, 178, 0, 0.4)';
    case 'high':
      return 'rgba(204, 75, 55, 0.4)';
    default:
      return 'transparent';
  }
};

export const SurvivalAnalysisDetailsCard: React.FC<
  SurvivalAnalysisDetailsCardProps
> = ({
  components,
  assetsPerSubComponent,
  enableDarkTheme,
  displayMode,
  detailType,
  eolDates,
  handleModalSuccess,
  healthData,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const isUserAllowed = useRoleBasedAccess();

  const textColor = palette.textPrimary;

  const [isSurvivalAnalysisEditModal, setIsSurvivalAnalysisEditModal] =
    useState(false);
  const [selectedComponent, setSelectedComponent] = useState<any>({});

  const [editedComponentId, setEditedComponentId] = useState<any>('');
  const handleModalSuccessItem = () => {
    setEditedComponentId(selectedComponent);
    setIsSurvivalAnalysisEditModal(false);
    handleModalSuccess();
  };

  const processedComponentItems = useMemo(() => {
    const processed: Record<string, any> = {};

    try {
      components.forEach((item) => {
        const key = `${item.asset_id}-${item.components_category}-${item.sub_component}`;

        if (!processed[key]) {
          // Initialize the object with other_actions array
          const otherActions: Array<{
            actions_performed: string;
            date: string;
          }> = [];
          if (
            !item?.actions_performed?.toLowerCase()?.includes('repair') &&
            !item?.actions_performed?.toLowerCase()?.includes('replace')
          ) {
            otherActions.push({
              actions_performed: item.actions_performed,
              date: item.date_of_orders,
            });
          }
          processed[key] = { ...item, other_actions: otherActions };
        } else {
          const existing = processed[key];

          // If actions_performed is REPAIR or REPLACE, update eolDate
          if (
            item?.actions_performed?.toLowerCase()?.includes('repair') ||
            item?.actions_performed?.toLowerCase()?.includes('replace')
          ) {
            const keys = Object.keys(item);

            keys.forEach((key) => {
              if (key !== 'actions_performed') {
                existing[key] = item[key];
              }
            });
          } else {
            // Add to other_actions if it's not REPAIR or REPLACE
            existing.other_actions.push({
              actions_performed: item.actions_performed,
              date: item.date_of_orders,
            });
          }
        }
      });

      return Object.values(processed);
    } catch (error) {
      console.log(error);
      return Object.values(processed);
    }
  }, [components]);

  return (
    <>
      <SurvivalAnalysisEditModal
        key={`${isSurvivalAnalysisEditModal.toString()}-edit-date`}
        open={isSurvivalAnalysisEditModal}
        handleAbort={() => {
          setIsSurvivalAnalysisEditModal(false);
        }}
        title={t('edit_high_risk_date')}
        enableDarkTheme={enableDarkTheme}
        component={selectedComponent}
        handleModalSuccess={handleModalSuccessItem}
      />

      <Grid container spacing={1}>
        {processedComponentItems.map((component, index) => {
          const componentEol = eolDates?.find(
            (item) =>
              item.components_category === component.components_category &&
              item.sub_components_category === component.sub_component &&
              item.asset_id === component.asset_id
          );

          const subComponentName = component.sub_component;
          const componentName = component?.components_category;
          const isComponentAndSubComponentSame =
            subComponentName?.toLowerCase() === componentName?.toLowerCase();
          let recommendedAction = 'Repair/Replace';
          const detailsHealth = healthData.find(
            (item) =>
              item.bumper_id === component.asset_id &&
              item.component === component.components_category &&
              item.sub_component === component.sub_component
          );
          if (detailsHealth) {
            recommendedAction =
              detailsHealth?.recommended_action ?? 'Repair/Replace';
          }
          return (
            <Grid
              item
              xs={6}
              sm={6}
              md={displayMode === 'grid' ? 4 : 12}
              key={index}
            >
              <Card
                className="card"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: backgroundColor(
                    component.work_order_age_category
                  ),
                  color: textColor,
                  padding: 0,
                  gap: 0,
                  border:
                    component.asset_id === editedComponentId.asset_id &&
                    component.components_category ===
                      editedComponentId.components_category
                      ? '2px solid #FFFF6E'
                      : 'none', // Add border conditionally
                }}
              >
                <CardContent
                  sx={{
                    padding: 0,
                    gap: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 1,
                        minWidth: '150px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Box>
                          <GetIcons
                            icon={
                              detailType === 'Assets'
                                ? componentsImage
                                : assetImage
                            }
                            enableDarkTheme={enableDarkTheme}
                          />
                        </Box>
                        <Box>
                          <Tooltip
                            title={`${component.asset_id} ${componentName}`}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                paddingLeft: 1,
                              }}
                            >
                              <Typography variant="small">
                                {component.asset_id}
                              </Typography>
                              <Typography
                                variant="small"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '100px',
                                }}
                              >
                                {componentName}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Box>
                      </Box>

                      {isComponentAndSubComponentSame ? null : (
                        <Typography variant="small" color="grey">
                          {t('sub_component_3')}
                        </Typography>
                      )}
                      <Typography variant="small">
                        {isComponentAndSubComponentSame ? '' : subComponentName}
                      </Typography>
                      <Typography variant="small" color="grey">
                        {t('installation_date')}
                      </Typography>
                      <Typography variant="small">
                        {convertStdDateString(component.date_of_orders)}
                      </Typography>
                      <Typography variant="small" color="grey">
                        {t('age')}
                      </Typography>
                      <Typography variant="small">
                        {formatAgeInDays(component.work_order_age_in_days)}
                      </Typography>
                      <Typography variant="small">
                        <var>
                          <span style={{ color: 'grey' }}>
                            {component.work_order_age_category === 'eol' ||
                            component.work_order_age_category === 'high'
                              ? 'Expected EOL: '
                              : component.work_order_age_category === 'med'
                              ? 'High Risk: '
                              : component?.work_order_age_category === 'low'
                              ? 'Medium Risk: '
                              : 'Expected EOL: '}
                          </span>
                        </var>
                        {convertStdDateString(
                          component.work_order_age_category === 'eol' ||
                            component.work_order_age_category === 'high'
                            ? component?.eol_date
                            : component.work_order_age_category === 'med'
                            ? addDaysToDate(
                                component.date_of_orders,
                                component.yellow_red
                              )
                            : component.work_order_age_category === 'low'
                            ? addDaysToDate(
                                component.date_of_orders,
                                component.green_yellow
                              )
                            : component?.eol_date
                        )}
                      </Typography>
                      {componentEol?.actions_performed ? (
                        <>
                          <Typography variant="small" color="grey">
                            {t('action_to_perform')}
                          </Typography>
                          <Typography variant="small">
                            {recommendedAction}
                          </Typography>
                        </>
                      ) : (
                        <>
                          {/*  temp added will be removed #r1 */}
                          <Typography variant="small" color="grey">
                            {t('action_to_perform')}
                          </Typography>
                          <Typography variant="small">
                            {recommendedAction}
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Paper
                      sx={{
                        display: 'flex',
                        backgroundColor: enableDarkTheme
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.05)',
                        paddingLeft: 2,
                        paddingRight: 0,
                        width: '100%',
                        // minWidth: '150px',
                      }}
                    >
                      <SurvivalAnalysisDetailsChart
                        enableDarkTheme={enableDarkTheme}
                        workOrderAgeCategory={component.work_order_age_category}
                        component={component}
                        assetsPerSubComponent={assetsPerSubComponent}
                      />

                      {/* todo #r1 = >  for now we are not doing anything on eol update, we need to rework on this after himashi get's some clearity */}
                      {detailType === 'Assets' &&
                        component.work_order_age_category === 'eol' && (
                          <div>
                            <PermissionTooltip show={!isUserAllowed}>
                              <IconButton
                                sx={{
                                  color: palette.textPrimary,
                                  '&:hover': {
                                    backgroundColor: 'transparent', // Remove hover background color
                                  },
                                  // alignItems: 'flex-start',
                                }}
                                onClick={() => {
                                  setIsSurvivalAnalysisEditModal(true);
                                  setSelectedComponent(component);
                                }}
                                disabled={!isUserAllowed}
                              >
                                <Edit />
                              </IconButton>
                            </PermissionTooltip>
                          </div>
                        )}
                    </Paper>
                  </Box>
                </CardContent>
                <CardActions
                  sx={{
                    padding: 0,
                    gap: 0,
                  }}
                ></CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};
